import { api } from '@verifime/api-definition';
import {
  PERMISSION_NOTICE_BODY,
  PERMISSION_NOTICE_TITLE,
  REQUEST_UNAVAILABLE_BODY,
  REQUEST_UNAVAILABLE_TITLE,
  enqueErrorNotification,
  enqueSuccessNotification,
} from '@verifime/components';
import { useEffect, useState } from 'react';
import { isRefusal } from './isRefusal';
import { requestStateFromTasks, TRequestState } from './requestState';

export type TUseFaceCheckTasksParams = {
  customerId?: string;
  /** Called when the lookup fails. This package has no logging surface of its own. */
  onError?: (error: unknown) => void;
};

/**
 * Whether this caller may ask this customer for a face check, as the task lookup answered it.
 *
 * **Three values, not two.** `unknown` is not a polite `denied`: it is the state where the lookup
 * could not tell us, and the two lead to different copy on the card. Folding them together would
 * either tell a client their customer has not authorised them on the strength of a server fault,
 * or offer a control that is certain to be refused.
 */
export type TUseFaceCheckTasks = {
  status: 'loading' | 'error' | 'ready';
  /**
   * Always present, even while loading or after a failure, and defaulting to nothing outstanding.
   *
   * The alternative, leaving it undefined until it resolves, pushes the same guard into every caller
   * and invites one of them to render the button anyway. Defaulting to "nothing outstanding" is the
   * safer of the two possible wrong answers: claiming a repeat that never happened tells a client
   * their customer already has a request waiting, and they may hold off chasing because of it.
   */
  requestState: TRequestState;
  /**
   * Asks the customer to complete a fresh face check. Fire-and-forget: the caller wires this
   * straight to a button's `onClick` and reads the result off `requestState`, which this updates in
   * place once the request lands.
   *
   * This is the one action every caller of this hook needs, with nothing page-specific about it
   * (unlike, say, navigating to an assessment): it only ever needs the customer id this hook already
   * holds. Owning it here rather than taking it as a prop from further up is what makes the button
   * work without anything to wire, which is the defect this hook exists to close off (see
   * `CustomerConfidenceCards`'s own docblock for the history).
   */
  requestFaceCheck: () => void;
};

const NOTHING_OUTSTANDING: TRequestState = { hasOutstandingRequest: false };

/**
 * What to tell a client whose request was not sent.
 *
 * **Two sentences, and only the second one branches.** Every non-`200` on this path is the same
 * thing to a client — the request cannot be made — so it opens the same way, which is the owner's
 * ruling of 2026-08-26: "we cannot make this request succeed, so it should use the same solution".
 * What differs is whether we can name a remedy. A `403` can: the customer grants permission.
 * Nothing else can, and saying so anyway would send a staff member whose session has just expired
 * after an authorisation that was never the obstacle.
 *
 * **Neither branch renders the error.** The line this replaced produced
 * `Request failed [AxiosError: Request failed with status code 403]` on a refusal and
 * `Request failed [object Object]` on a server fault. An earlier revision of this function fixed
 * only the first: it fell back to the error's own `message`, which is a resolved, user-facing
 * sentence for a 5xx (the interceptor rewrites those from `SERVER_ERRORS`) but is axios's own
 * `"Request failed with status code 401"` for every 4xx, since `interceptorWithToken` rethrows
 * those untouched. That reintroduced, for one class of failure, exactly the string this change
 * exists to remove. Raised in cross review on 2026-08-26.
 *
 * **Reached rarely, and that is the point.** The control is only offered on a `200`, so a client
 * gets here by pressing under a lookup that had not resolved, or by holding a page open across a
 * permission change or a session expiry — precisely when a status code helps least.
 */
function requestFailureMessage(error: unknown): string {
  return isRefusal(error)
    ? `${PERMISSION_NOTICE_TITLE}. ${PERMISSION_NOTICE_BODY}`
    : `${REQUEST_UNAVAILABLE_TITLE}. ${REQUEST_UNAVAILABLE_BODY}`;
}

function fetchRequestState(customerId: string): Promise<TRequestState> {
  return api
    .getV1taskcustomerCustomerId({ params: { customerId }, queries: { type: 'FACE_CHECK' } })
    .then((tasks) => requestStateFromTasks(Array.isArray(tasks) ? tasks : []));
}

type TFaceCheckTasksState = Omit<TUseFaceCheckTasks, 'requestFaceCheck'>;

/**
 * Reads the customer's face check tasks and reduces them to the request state.
 *
 * Filtered to `FACE_CHECK` at the endpoint rather than in the reducer: a customer's task list also
 * carries permission requests and document tasks, and an open one of those would otherwise put
 * `REQUEST AGAIN` on the biometric card for a request that is not a face check.
 */
export function useFaceCheckTasks({
  customerId,
  onError,
}: TUseFaceCheckTasksParams): TUseFaceCheckTasks {
  const [state, setState] = useState<TFaceCheckTasksState>({
    status: 'loading',
    requestState: NOTHING_OUTSTANDING,
  });

  useEffect(() => {
    // Reset before anything else. Without this the previous customer's request link and status
    // survive the switch, so a client could copy and send one customer's tracking link from
    // another customer's card, with no loading affordance to warn them.
    setState({ status: 'loading', requestState: NOTHING_OUTSTANDING });

    if (!customerId) {
      return;
    }

    let abandoned = false;

    fetchRequestState(customerId)
      .then((requestState) => {
        if (!abandoned) {
          setState({ status: 'ready', requestState });
        }
      })
      .catch((error: unknown) => {
        if (abandoned) {
          return;
        }

        setState({ status: 'error', requestState: NOTHING_OUTSTANDING });

        // Every failure of this call is reported now, refusals included. It used to swallow the
        // `403` because the card read this call for permission and a refusal was the expected
        // answer rather than a fault. It no longer does — permission comes from
        // `useRequestPermission` — so this call has gone back to being about tasks only, and
        // a failure of it is a failure of reading them, whatever its status code.
        onError?.(error);
      });

    return () => {
      abandoned = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Returns void rather than the promise: every caller wires this straight to a button's `onClick`,
  // which cannot await anything, and gets the outcome through `requestState` instead, which this
  // updates once the request lands.
  const requestFaceCheck = () => {
    if (!customerId) {
      return;
    }

    api
      .postV1taskcustomerCustomerId({ type: 'FACE_CHECK' }, { params: { customerId } })
      .then(() => {
        // Dispatched before the notification, deliberately: this is the substantive part of a
        // successful request, and it must not be at the mercy of the notification call, which is
        // a side effect the state update should not depend on succeeding.
        //
        // Refetched rather than built from the create response. `TaskCreatedResponse` (this call's
        // response) carries no `trackingReferenceUrl`, unlike the list endpoint's
        // `TaskLookupResponse`: trusting it would flip the verb to REQUEST AGAIN while the
        // no-outstanding-requests line kept showing, because there would be no link to replace it
        // with. Going back through the same reducer as the initial load is what keeps the verb, the
        // link and the provenance row agreeing with each other, the way `requestActionCopy`'s
        // docblock says they must.
        //
        // A failure here is reported but not surfaced as a second "request failed" notification:
        // the request itself succeeded, only the refresh of what is now on screen did not, so the
        // success notification told the client the truth.
        fetchRequestState(customerId)
          .then((requestState) => setState((current) => ({ ...current, requestState })))
          .catch((error: unknown) => onError?.(error));

        enqueSuccessNotification('Request success');
      })
      .catch((error: unknown) => {
        enqueErrorNotification(requestFailureMessage(error));
        onError?.(error);
      });
  };

  return { ...state, requestFaceCheck };
}
